import { useState, useEffect, useMemo } from 'react';
import {
  Client, BilledMonth, InvoiceRow, SummaryPeriod,
  MonthlyRow, ClientSummaryRecord, RollingWindow,
} from '../types/common';

const toNum = (v: unknown): number => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const pctChange = (curr: number, prev: number | null): number | null =>
  prev !== null && prev !== 0 ? ((curr - prev) / prev) * 100 : null;

const shortYM = (ym: string): string => {
  const [y, m] = ym.split('-');
  const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${mon[parseInt(m, 10) - 1]} '${y.slice(2)}`;
};

// Helper: Check if a billing year-month (YYYY-MM) matches FY or calendar year filters
// fy format is "2025-26" (4-digit start year, 2-digit end suffix)
const matchesYearFilters = (billingYM: string, fyYears: string[], calendarYears: string[]): boolean => {
  if (fyYears.length === 0 && calendarYears.length === 0) return true;
  if (!billingYM || !billingYM.includes('-')) return true;

  const parts = billingYM.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);

  // Check FY filter (Indian FY: Apr-Mar)
  if (fyYears.length > 0) {
    for (const fy of fyYears) {
      // fy is "2025-26" → split gives ["2025", "26"]
      const fyParts = fy.split('-');
      const startYearFull = parseInt(fyParts[0], 10); // 2025
      const endYearFull = startYearFull + 1;           // 2026
      // Apr-Dec belong to start year; Jan-Mar belong to end year
      const isInFY = (month >= 4 && year === startYearFull) || (month <= 3 && year === endYearFull);
      if (isInFY) return true;
    }
    return false;
  }

  // Check calendar year filter
  if (calendarYears.length > 0) {
    return calendarYears.includes(String(year));
  }

  return true;
};

function mapToClient(r: Record<string, unknown>, summaryMap: Map<number, ClientSummaryRecord>): Client {
  const id = toNum(r.cli_contact_id);
  const s = summaryMap.get(id);
  const isNew = toNum(r.cli_is_new_client_12m) === 1 || toNum(r.cli_is_new_client_in_period) === 1;
  const isRecurring = toNum(r.cli_is_recurring_client_12m) === 1 || toNum(r.cli_is_recurring_client_lifetime) === 1;

  return {
    id,
    name: String(r.cli_contact_name ?? '').trim(),
    isNew,
    isRecurring,
    revenue: toNum(r.cli_period_revenue_invoiced_usd),
    collectedRevenue: toNum(r.cli_period_revenue_collected_usd),
    invoiced: s?.totalInvoiced ?? toNum(r.cli_period_revenue_invoiced_usd),
    outstanding: s?.outstanding ?? 0,
    deposit: s?.deposit ?? 0,
    depositAdjustment: s?.depositAdjustment ?? 0,
    netDeposit: s?.netDeposit ?? 0,
    firstInvoice: (r.cli_first_invoice_sent_date as string) ?? (r.cli_first_invoice_sent_in_period as string) ?? undefined,
    lastInvoice: (r.cli_last_invoice_sent_in_period as string) ?? undefined,
    status: String(r.cli_client_status ?? '').toLowerCase() === 'active' ? 'active' : 'inactive',
    tier: (r.cli_client_tier as string) ?? undefined,
    region: (r.cli_region as string) ?? undefined,
    invoiceCount: toNum(r.cli_period_invoice_count),
    distinctBilledMonths: toNum(r.cli_distinct_billed_months_12m),
    paymentRatio: s?.paymentRatio,
    riskLevel: s?.riskLevel,
    filename: s?.filename,
  };
}

function mapToBilledMonth(r: Record<string, unknown>): BilledMonth {
  const ym = String(r.mm_billing_year_month ?? '');
  return {
    ym,
    totalInvoiced: toNum(r.mm_total_invoiced_usd),
    newRevenue: toNum(r.mm_new_logo_invoiced_usd),
    repeatRevenue: toNum(r.mm_repeat_invoiced_usd),
    newClients: toNum(r.mm_new_clients_in_month),
    existingClients: toNum(r.mm_existing_clients_in_month),
    pctNew: toNum(r.mm_pct_invoiced_new_logo),
    xLabel: shortYM(ym),
  };
}

function mapToInvoiceRow(r: Record<string, unknown>): InvoiceRow {
  return {
    ym: String(r.inv_billing_year_month ?? r.mm_billing_year_month ?? ''),
    clientId: r.cli_contact_id != null ? toNum(r.cli_contact_id) : null,
    clientName: String(r.cli_contact_name ?? '').trim(),
    isNewLogoMonth: toNum(r.inv_is_new_logo_month) === 1,
    isRepeatMonth: toNum(r.inv_is_repeat_month) === 1,
  };
}

interface UseClientDataResult {
  clients: Client[];
  billedMonths: BilledMonth[];
  monthlyRows: MonthlyRow[];
  invoicesByYM: Map<string, InvoiceRow[]>;
  summaryPeriod: SummaryPeriod | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export const useClientData = (
  rollingWindow: RollingWindow = 12,
  fyYears: string[] = [],
  calendarYears: string[] = []
): UseClientDataResult => {
  const [raw, setRaw] = useState<Record<string, unknown>[]>([]);
  const [summaryRecords, setSummaryRecords] = useState<ClientSummaryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetch('/json-data/client_acquisition_retention.json').then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
      fetch('/clients-summary.json').then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
    ])
      .then(([retData, sumData]) => {
        if (cancelled) return;
        setRaw(Array.isArray(retData) ? retData : []);
        setSummaryRecords(Array.isArray(sumData?.clients) ? sumData.clients : []);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(String(err?.message ?? err));
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tick]);

  const summaryMap = useMemo(() => {
    const m = new Map<number, ClientSummaryRecord>();
    summaryRecords.forEach(s => m.set(s.id, s));
    return m;
  }, [summaryRecords]);

  // CLIENT records are rolling-window summaries — not filtered by FY/year
  const clients = useMemo(() =>
    raw
      .filter(r => r.record_type === 'CLIENT' && toNum(r.rolling_window_months) === rollingWindow)
      .map(r => mapToClient(r, summaryMap)),
  [raw, summaryMap, rollingWindow]);

  const billedMonths = useMemo(() =>
    raw
      .filter(r => {
        if (r.record_type !== 'BILLED_MONTH' || toNum(r.mm_rolling_window_months) !== rollingWindow || !r.mm_billing_year_month) return false;
        const ym = String(r.mm_billing_year_month);
        return matchesYearFilters(ym, fyYears, calendarYears);
      })
      .map(r => mapToBilledMonth(r))
      .sort((a, b) => a.ym.localeCompare(b.ym)),
  [raw, rollingWindow, fyYears, calendarYears]);

  const monthlyRows = useMemo((): MonthlyRow[] =>
    billedMonths.map((bm, i) => ({
      ...bm,
      repeatSharePct: bm.totalInvoiced > 0 ? (bm.repeatRevenue / bm.totalInvoiced) * 100 : 0,
      momPct: i > 0 ? pctChange(bm.totalInvoiced, billedMonths[i - 1].totalInvoiced) : null,
    })),
  [billedMonths]);

  const invoicesByYM = useMemo(() => {
    const m = new Map<string, InvoiceRow[]>();
    raw
      .filter(r => {
        if (r.record_type !== 'INVOICE' || toNum(r.rolling_window_months) !== rollingWindow) return false;
        const ym = String(r.inv_billing_year_month ?? r.mm_billing_year_month ?? '');
        return matchesYearFilters(ym, fyYears, calendarYears);
      })
      .forEach(r => {
        const row = mapToInvoiceRow(r);
        if (!row.ym) return;
        if (!m.has(row.ym)) m.set(row.ym, []);
        m.get(row.ym)!.push(row);
      });
    return m;
  }, [raw, rollingWindow, fyYears, calendarYears]);

  const summaryPeriod = useMemo((): SummaryPeriod | null => {
    const r = raw.find(r => r.record_type === 'SUMMARY_PERIOD' && toNum(r.rolling_window_months) === rollingWindow) as any;
    if (!r) return null;
    return {
      rolling_window_months: rollingWindow,
      clients_active_in_period: toNum(r.clients_active_in_period),
      new_clients: toNum(r.new_clients),
      existing_clients: toNum(r.existing_clients),
      recurring_clients_12m: toNum(r.recurring_clients_12m),
      total_revenue_invoiced_usd: toNum(r.total_revenue_invoiced_usd),
      total_revenue_collected_usd: toNum(r.total_revenue_collected_usd),
      new_logo_revenue_invoiced_usd: toNum(r.new_logo_revenue_invoiced_usd),
      repeat_revenue_invoiced_usd: toNum(r.repeat_revenue_invoiced_usd),
      pct_invoiced_revenue_new_logo: toNum(r.pct_invoiced_revenue_new_logo),
      pct_invoiced_revenue_repeat: toNum(r.pct_invoiced_revenue_repeat),
      analysis_period_label: String(r.analysis_period_label ?? ''),
    };
  }, [raw, rollingWindow]);

  return { clients, billedMonths, monthlyRows, invoicesByYM, summaryPeriod, loading, error, reload: () => setTick(t => t + 1) };
};
